import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BusyButton, CopyButton, Field, PageHeader, Switch, useToast } from "../../components/ui";
import { DirtyActions } from "../../components/UnsavedChanges";
import { ErrorState, Skeleton } from "../../components/States";
import { signInAdminApi, signInAdminKeys, type SignInConfig, type SignInProviderID, type SignInProviderInput } from "./api";
import "../mail-admin/mail-admin.css";

const PROVIDERS: Array<{ id: SignInProviderID; label: string; hint: string }> = [
  { id: "google", label: "Google", hint: "A web application client in Google Cloud. Register the redirect address below." },
  { id: "microsoft", label: "Microsoft", hint: "An app registration in Microsoft Entra. Add the redirect address under Web." },
  { id: "github", label: "GitHub", hint: "An OAuth app in GitHub developer settings. Register the redirect address below." },
];

type Row = SignInProviderInput;

function rowsFrom(c: SignInConfig): Row[] {
  return PROVIDERS.map((p) => {
    const got = c.providers.find((x) => x.id === p.id);
    return { id: p.id, enabled: got?.enabled ?? false, clientId: got?.clientId ?? "", clientSecret: "" };
  });
}

function same(a: Row[], b: Row[]) {
  return a.every((row, i) => row.enabled === b[i]?.enabled && row.clientId === b[i]?.clientId && !row.clientSecret);
}

export function SignInAdminPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery({ queryKey: signInAdminKeys.config, queryFn: signInAdminApi.config });
  const [rows, setRows] = useState<Row[] | null>(null);

  useEffect(() => {
    if (cfg.data) setRows((r) => r ?? rowsFrom(cfg.data));
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: (body: Row[]) => signInAdminApi.save(body),
    onSuccess: (next) => {
      qc.setQueryData(signInAdminKeys.config, next);
      setRows(rowsFrom(next));
      toast.push("success", "Sign-in settings saved.");
    },
    onError: (e: Error) => toast.push("error", e.message),
  });

  if (cfg.isLoading || !rows || !cfg.data) {
    return (
      <Shell>
        <Skeleton />
      </Shell>
    );
  }
  if (cfg.isError) {
    return (
      <Shell>
        <ErrorState error={cfg.error} retry={() => void cfg.refetch()} />
      </Shell>
    );
  }

  const data = cfg.data;
  const saved = rowsFrom(data);
  const dirty = !same(rows, saved);
  const ready = rows.every((row) => {
    if (!row.enabled) return true;
    const stored = data.providers.find((p) => p.id === row.id);
    if (stored?.usesApp) return true;
    return !!row.clientId.trim() && (!!row.clientSecret || !!stored?.hasSecret);
  });
  const set = (id: SignInProviderID, patch: Partial<Row>) => setRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const on = rows.filter((r) => r.enabled).length;

  return (
    <Shell>
      <div className="card section">
        <div className="mail-admin-head">
          <div>
            <h2>Accounts</h2>
            <p className="muted small">People can use these in addition to email. A provider stays off until it has a client ID and secret and the switch is on.</p>
          </div>
          <span className={`badge ${on > 0 ? "ok" : "warn"}`} data-testid="signin-source">
            {on > 0 ? `${on} on` : "All off"}
          </span>
        </div>
        {data.source === "deployment" && (
          <p className="muted small" data-testid="signin-deployment-note">
            These came with the deployment. Save them to keep them, or change them here.
          </p>
        )}
        <div className="settings-stack">
          {PROVIDERS.map((p) => {
            const row = rows.find((r) => r.id === p.id)!;
            const stored = data.providers.find((x) => x.id === p.id);
            const hint = stored?.usesApp ? "People sign in with the GitHub registration. Organizations use that same registration to connect repositories." : p.hint;
            return (
              <section key={p.id} className="card" style={{ padding: "var(--space-4)" }}>
                <Switch id={`signin-${p.id}`} checked={row.enabled} onChange={(v) => set(p.id, { enabled: v })} label={p.label} hint={hint} />
                {stored?.usesApp ? null : (
                <div className="settings-stack">
                  <Field label="Client ID">
                    {(f) => <input {...f} className="input mono" value={row.clientId} onChange={(e) => set(p.id, { clientId: e.target.value })} autoComplete="off" />}
                  </Field>
                  <Field label="Client secret" hint={stored?.hasSecret ? "A secret is stored. Leave this blank to keep it." : undefined}>
                    {(f) => (
                      <input
                        {...f}
                        className="input mono"
                        type="password"
                        value={row.clientSecret ?? ""}
                        onChange={(e) => set(p.id, { clientSecret: e.target.value })}
                        placeholder={stored?.hasSecret ? "••••••••••••" : undefined}
                        autoComplete="off"
                      />
                    )}
                  </Field>
                  <Field label="Redirect address" hint="Register this exact address with the provider.">
                    {(f) => (
                      <span style={{ display: "flex", gap: 8 }}>
                        <input {...f} className="input mono" readOnly value={stored?.callbackUrl ?? ""} onFocus={(e) => e.currentTarget.select()} />
                        <CopyButton value={stored?.callbackUrl ?? ""} label="Copy" />
                      </span>
                    )}
                  </Field>
                </div>
                )}
              </section>
            );
          })}
          <DirtyActions dirty={dirty} onDiscard={() => setRows(saved)}>
            <BusyButton className="btn primary" busy={save.isPending} busyLabel="Saving…" onClick={() => save.mutate(rows)} disabled={!dirty || !ready}>
              Save sign-in
            </BusyButton>
          </DirtyActions>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mail-admin">
      <PageHeader title="Sign-in" subtitle="Google, Microsoft, and GitHub" />
      {children}
    </div>
  );
}
